import { useParams, Link } from 'react-router-dom';
import SEOHelmet from '@/components/base/SEOHelmet';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-4">
          <i className="ri-article-line text-2xl"></i>
        </div>
        <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h1>
        <p className="text-foreground-600 mb-6">该文章可能已被移除或链接有误</p>
        <Link
          to="/"
          className="inline-flex px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md font-medium text-sm transition-colors whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://91-safety-guide.readdy.ai/article/${article.slug}`,
    },
    publisher: {
      '@type': 'Organization',
      name: '91安全指南',
      logo: {
        '@type': 'ImageObject',
        url: 'https://91-safety-guide.readdy.ai/logo.png',
      },
    },
  };

  return (
    <>
      <SEOHelmet
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://91-safety-guide.readdy.ai/article/${article.slug}`}
        ogType="article"
        articlePublishedTime={article.publishedAt}
        articleModifiedTime={article.updatedAt}
        articleAuthor={article.author}
        articleTags={article.tags}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />

      {/* Article Header */}
      <section className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">
              {article.categoryName}
            </span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700"
              >
                {tag}
              </span>
            ))}
          </div>
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
            {article.title}
          </h1>
          <p className="text-base md:text-lg text-foreground-600 mb-6 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i> {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i> 发布于 {article.publishedAt}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-refresh-line"></i> 更新于 {article.updatedAt}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i> 阅读 {article.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Article Image */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-6 relative z-10">
        <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top"
            title={article.title}
          />
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-4 md:px-6 py-10">
        <div
          className="prose-content text-foreground-800 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-background-100 py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.slug}`}
                  className="group p-5 rounded-lg bg-background-50 border border-background-200/70 transition-all duration-300"
                >
                  <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                    {related.categoryName}
                  </span>
                  <h3 className="font-heading font-semibold text-foreground-900 mt-3 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {related.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {related.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}